import React, { useState, useMemo, useCallback } from 'react';
import { redbookSummary, fetchTopicDetail } from './api/redbookData';

// Display a stored value per its page's format token (see data/redbook/pages/*.meta.json):
//   text | rank | int | int_signed | dec<n> | pct<n> | pct<n>_signed | usd<n> | cents<n> | ratio<n>
// Values are stored clean (percent magnitude, no symbols); the token supplies the display.
const TOKEN_RE = /^(rank|int|dec|pct|usd|cents|ratio)(\d)?(_signed)?$/;
// Tokens from the 2025 edition's documents, so an older collection still renders.
const LEGACY_TOKENS = { currency: 'usd0', percent: 'pct2', number: 'dec2', ratio: 'ratio2', cents: 'cents2' };

function formatValue(value, format, is_decimal = false) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'text') return String(value);
  let num = Number(value);
  if (Number.isNaN(num)) return String(value);
  if (format === 'percent' && is_decimal) num *= 100;
  const m = TOKEN_RE.exec(LEGACY_TOKENS[format] || format || '');
  if (!m) return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const [, kind, digitsStr, signed] = m;
  const digits = digitsStr === undefined ? (kind === 'dec' ? 2 : 0) : Number(digitsStr);
  const fixed = (n) => n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const sign = num < 0 ? '-' : signed && num > 0 ? '+' : '';
  const abs = fixed(Math.abs(num));
  switch (kind) {
    case 'rank': return String(Math.round(num));
    case 'usd': return `${sign}$${abs}`;
    case 'pct': return `${sign}${abs}%`;
    case 'cents': return `${sign}${abs}¢`;
    default: return `${sign}${abs}`;
  }
}

// Ordinal suffix for ranks
function getOrdinal(n) {
  if (n === null || n === undefined) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Category display order
const CATEGORY_ORDER = [
  'Economy', 'Business Climate', 'Workforce', 'Demographics',
  'Education', 'Innovation', 'Government', 'Housing',
  'Energy', 'Infrastructure', 'Quality of Life'
];

// Category colors for visual distinction
const CATEGORY_COLORS = {
  'Economy': '#245BA9',
  'Business Climate': '#D97706',
  'Workforce': '#059669',
  'Demographics': '#7C3AED',
  'Education': '#DC2626',
  'Innovation': '#0891B2',
  'Government': '#4B5563',
  'Housing': '#EA580C',
  'Energy': '#16A34A',
  'Infrastructure': '#6366F1',
  'Quality of Life': '#DB2777',
};

// Rank color (green = good rank, red = bad rank, relative to total)
function getRankColor(rank, total) {
  if (!rank || !total) return '#4B5563';
  const ratio = rank / total;
  if (ratio <= 0.2) return '#059669'; // Top 20% = green
  if (ratio <= 0.4) return '#16A34A'; // Top 40% = light green
  if (ratio <= 0.6) return '#D97706'; // Middle = amber
  if (ratio <= 0.8) return '#EA580C'; // Bottom 40% = orange
  return '#DC2626'; // Bottom 20% = red
}

function entityLabel(entityType, plural = true) {
  const labels = { state: 'state', metro: 'metro area', city: 'city', airport: 'airport' };
  const label = labels[entityType] || entityType || 'state';
  if (!plural) return label;
  return label === 'city' ? 'cities' : `${label}s`;
}

// Connecticut's value on the summary rows; pages ranked by a published rank
// (CNBC, Tax Foundation) already show it as the rank, so nothing extra.
function ctValueLabel(item) {
  if (item.ct_value === null || item.ct_value === undefined) return '';
  if (item.primary_format === 'rank') return '';
  return formatValue(item.ct_value, item.primary_format, item.primary_is_decimal);
}

function ctRankLabel(item) {
  if (item.ct_rank) return getOrdinal(item.ct_rank);
  return item.no_ranking ? 'n/a' : 'N/R';
}

const RedbookView = () => {
  const [view, setView] = useState('highlights'); // 'highlights', 'all', 'detail'
  const [, setSelectedTopic] = useState(null);
  const [topicDetail, setTopicDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);

  const allRankings = useMemo(() => redbookSummary?.all_rankings || [], []);
  const highlights = redbookSummary?.highlights || [];
  const edition = redbookSummary?.edition;

  // Group rankings by category
  const groupedRankings = useMemo(() => {
    const groups = {};
    for (const ranking of allRankings) {
      const cat = ranking.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(ranking);
    }
    // Sort by category order
    const ordered = {};
    for (const cat of CATEGORY_ORDER) {
      if (groups[cat]) ordered[cat] = groups[cat];
    }
    // Add any remaining categories
    for (const cat of Object.keys(groups)) {
      if (!ordered[cat]) ordered[cat] = groups[cat];
    }
    return ordered;
  }, [allRankings]);

  // Handle drilling into a topic
  const handleTopicClick = useCallback(async (topicId) => {
    setLoading(true);
    setView('detail');
    setSelectedTopic(topicId);
    setSelectedMetric(null);
    const detail = await fetchTopicDetail(topicId);
    setTopicDetail(detail);
    // Default to the page's own ranking. When the page is ranked by a
    // published rank rather than a value column, keep the row order as is.
    const metrics = detail?.available_metrics || [];
    const primary = metrics.find((m) => m.is_primary && m.format !== 'text');
    setSelectedMetric(primary ? primary.key : null);
    setLoading(false);
  }, []);

  const metrics = useMemo(() => topicDetail?.available_metrics || [], [topicDetail]);
  const sortableMetrics = useMemo(() => metrics.filter((m) => m.format !== 'text'), [metrics]);

  // Entity rows in display order, each with the rank to show in the # column.
  // Ranks come from the page when it publishes one for the chosen metric
  // (e.g. "Change Rank"); otherwise the order is computed from the values in
  // the metric's own direction (rank 1 = most favorable). Reference rows
  // (United States, District of Columbia) always sit unranked at the bottom.
  const entityList = useMemo(() => {
    if (!topicDetail) return [];
    const list = topicDetail.states || topicDetail.metros || topicDetail.cities || topicDetail.airports || [];
    const ranked = list.filter((e) => !e.is_reference);
    const reference = list.filter((e) => e.is_reference).map((e) => ({ ...e, shown_rank: null }));
    const metric = metrics.find((m) => m.key === selectedMetric);

    if (!metric || metric.format === 'text') {
      return [...ranked.map((e) => ({ ...e, shown_rank: e.rank })), ...reference];
    }

    let sortKey;
    let ascending;
    if (metric.rank_column) {
      sortKey = metric.rank_column;
      ascending = true;
    } else if (metric.format === 'rank') {
      sortKey = metric.key;
      ascending = true;
    } else {
      sortKey = metric.key;
      ascending = metric.rank_dir ? metric.rank_dir === 'asc' : Boolean(topicDetail.lower_is_better);
    }
    const hasValue = (e) => e[sortKey] !== null && e[sortKey] !== undefined;
    const sorted = ranked
      .filter(hasValue)
      .sort((a, b) => (ascending ? Number(a[sortKey]) - Number(b[sortKey]) : Number(b[sortKey]) - Number(a[sortKey])))
      .map((e, i) => ({
        ...e,
        shown_rank: metric.rank_column ? e[metric.rank_column] : metric.format === 'rank' ? e[metric.key] : i + 1,
      }));
    const unranked = ranked.filter((e) => !hasValue(e)).map((e) => ({ ...e, shown_rank: null }));
    return [...sorted, ...unranked, ...reference];
  }, [topicDetail, metrics, selectedMetric]);

  // Get entity name field
  const entityNameField = useMemo(() => {
    if (!topicDetail) return 'state';
    switch (topicDetail.entity_type) {
      case 'metro': return 'metro';
      case 'city': return 'city';
      case 'airport': return 'airport';
      default: return 'state';
    }
  }, [topicDetail]);

  // CT entity name for highlighting
  const ctSearchPatterns = ['Connecticut', ', CT', 'Bridgeport', 'Hartford', 'BDL'];

  const isCTEntity = (entity) => {
    const name = entity[entityNameField] || '';
    return ctSearchPatterns.some(p => name.includes(p));
  };

  // ============ HIGHLIGHTS VIEW ============
  if (view === 'highlights') {
    const displayItems = highlights.length > 0 ? highlights : allRankings.slice(0, 8);
    return (
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {displayItems.map((item) => (
            <div
              key={item.topic_id}
              onClick={() => handleTopicClick(item.topic_id)}
              className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4"
              style={{ borderLeftColor: CATEGORY_COLORS[item.category] || '#4B5563' }}
            >
              <div className="text-sm text-gray-500 mb-1">{item.category}</div>
              <div className="font-semibold text-sm mb-2 h-10 overflow-hidden">{item.display_name}</div>
              <div className="flex items-baseline justify-between">
                <span
                  className="text-2xl font-bold"
                  style={{ color: getRankColor(item.ct_rank, item.total_entities) }}
                >
                  {ctRankLabel(item)}
                </span>
                <span className="text-sm text-gray-600">{ctValueLabel(item)}</span>
              </div>
              <div className="text-xs text-gray-400 mt-1">
                of {item.total_entities} {entityLabel(item.entity_type)}
              </div>
            </div>
          ))}
        </div>
        <div className="text-center">
          <button
            onClick={() => setView('all')}
            className="px-6 py-3 rounded-lg text-white font-semibold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#245BA9' }}
          >
            View All {allRankings.length} Rankings{edition ? ` (${edition} edition)` : ''}
          </button>
        </div>
      </div>
    );
  }

  // ============ ALL RANKINGS VIEW ============
  if (view === 'all') {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={() => setView('highlights')}
            className="text-blue-600 hover:underline text-sm"
          >
            &larr; Back to Highlights
          </button>
        </div>

        {Object.entries(groupedRankings).map(([category, items]) => (
          <div key={category} className="mb-6">
            <h2
              className="text-lg font-bold mb-3 pb-1 border-b-2"
              style={{ borderBottomColor: CATEGORY_COLORS[category] || '#4B5563' }}
            >
              {category} ({items.length})
            </h2>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {items.map((item, idx) => (
                <div
                  key={item.topic_id}
                  onClick={() => handleTopicClick(item.topic_id)}
                  className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    idx < items.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex-1">
                    <span className="text-sm font-medium">{item.display_name}</span>
                    {item.data_year && <span className="text-xs text-gray-400 ml-2">({item.data_year})</span>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">{ctValueLabel(item)}</span>
                    <span
                      className="text-sm font-bold min-w-[50px] text-right"
                      style={{ color: getRankColor(item.ct_rank, item.total_entities) }}
                    >
                      {ctRankLabel(item)}
                    </span>
                    <span className="text-gray-300">&rsaquo;</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ============ TOPIC DETAIL VIEW ============
  if (view === 'detail') {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={() => { setView('all'); setTopicDetail(null); }}
            className="text-blue-600 hover:underline text-sm"
          >
            &larr; Back to All Rankings
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : topicDetail ? (
          <>
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span
                    className="text-xs font-semibold px-2 py-1 rounded"
                    style={{
                      backgroundColor: (CATEGORY_COLORS[topicDetail.category] || '#4B5563') + '20',
                      color: CATEGORY_COLORS[topicDetail.category] || '#4B5563'
                    }}
                  >
                    {topicDetail.category}
                  </span>
                  <h2 className="text-xl font-bold mt-2">{topicDetail.display_name}</h2>
                  <div className="text-sm text-gray-500 mt-1">
                    Source:{' '}
                    {topicDetail.link ? (
                      <a href={topicDetail.link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        {topicDetail.source}
                      </a>
                    ) : topicDetail.source}
                    {topicDetail.data_year && <> &middot; {topicDetail.data_year}</>}
                  </div>
                  {topicDetail.vintage && (
                    <div className="text-xs text-gray-400 mt-1">{topicDetail.vintage}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Connecticut</div>
                  <div
                    className="text-3xl font-bold"
                    style={{ color: getRankColor(topicDetail.ct_rank, topicDetail.total_entities) }}
                  >
                    {ctRankLabel(topicDetail)}
                  </div>
                  <div className="text-sm text-gray-600">
                    of {topicDetail.total_entities} {entityLabel(topicDetail.entity_type)}
                  </div>
                </div>
              </div>
              {topicDetail.ct_additional_info && (
                <p className="text-sm text-gray-600 mt-4 bg-gray-50 p-3 rounded">
                  {topicDetail.ct_additional_info}
                </p>
              )}
              {topicDetail.notes && (
                <details className="mt-3 text-xs text-gray-500">
                  <summary className="cursor-pointer select-none">About this data</summary>
                  <p className="mt-2 whitespace-pre-line">{topicDetail.notes}</p>
                  {topicDetail.source_table && <p className="mt-1">Table: {topicDetail.source_table}</p>}
                </details>
              )}
            </div>

            {/* Metric selector for multi-metric topics */}
            {sortableMetrics.length > 1 && (
              <div className="mb-4">
                <label className="text-sm text-gray-600 mr-2">Rank by:</label>
                <select
                  value={selectedMetric || ''}
                  onChange={(e) => setSelectedMetric(e.target.value || null)}
                  className="border rounded px-3 py-1 text-sm"
                >
                  {!sortableMetrics.some((m) => m.is_primary) && <option value="">Page order</option>}
                  {sortableMetrics.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.key} {m.is_primary ? '(primary)' : ''}
                    </option>
                  ))}
                </select>
                {topicDetail.no_ranking && (
                  <span className="text-xs text-gray-400 ml-2">This page is descriptive; ranks are computed for display only.</span>
                )}
              </div>
            )}

            {/* Rankings table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-3 font-semibold w-12">#</th>
                      <th className="text-left px-4 py-3 font-semibold">
                        {entityLabel(topicDetail.entity_type, false).replace(/^\w/, (c) => c.toUpperCase())}
                      </th>
                      {metrics.map((m) => (
                        <th
                          key={m.key}
                          className={`${m.format === 'text' ? 'text-left' : 'text-right'} px-4 py-3 font-semibold ${
                            m.key === selectedMetric ? 'bg-blue-50' : ''
                          }`}
                        >
                          {m.key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entityList.map((entity, idx) => {
                      const isct = isCTEntity(entity);
                      const rowClass = entity.is_reference
                        ? 'bg-gray-100 text-gray-500 italic'
                        : isct ? 'bg-blue-50 font-semibold' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                      return (
                        <tr key={idx} className={`border-b ${rowClass}`}>
                          <td className="px-4 py-2 text-gray-500">
                            {entity.shown_rank ?? '—'}
                          </td>
                          <td className="px-4 py-2">
                            {entity[entityNameField]}
                            {isct && !entity.is_reference && <span className="ml-1 text-blue-600 text-xs">(CT)</span>}
                          </td>
                          {metrics.map((m) => (
                            <td
                              key={m.key}
                              className={`${m.format === 'text' ? 'text-left' : 'text-right'} px-4 py-2 ${
                                m.key === selectedMetric ? 'bg-blue-50 font-medium' : ''
                              }`}
                            >
                              {formatValue(entity[m.key], m.format, m.is_decimal)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-red-500">Failed to load topic data</div>
        )}
      </div>
    );
  }

  return null;
};

export default RedbookView;
